import analyticsService from "@/services/analytics/analytics-service"
import intercomService from "@/services/intercom-service"
import { fetchIntercomJwt } from "@/composables/intercom"
import { useUserStore } from "@/store/user-store"
import { getActivePinia } from "pinia"
import { isCancelledError } from "@tanstack/vue-query"
import * as Sentry from "@sentry/vue"
import { queryClient } from "@/services/query-client"
import { QueryApiCacheKeys } from "@common/config/query-api-cache-keys"
import { memberBenefitsKeys } from "@/modules/home/public-queries"
import { healthWalletKeys } from "@/modules/health-wallet/public-queries"
import { useCareNavStore } from "@/store/care-nav-store"

async function handleLogInEvent() {
  const user = useUserStore().user
  if (!user) return
  analyticsService.loggedIn(user)
  await identifyIntercomUser(user.id)
}

async function handleLogOutEvent() {
  analyticsService.loggedOut()
  intercomService.resetToAnonymous()
  queryClient.removeQueries({ queryKey: [QueryApiCacheKeys.INTERCOM_JWT] })
  queryClient.removeQueries({ queryKey: [QueryApiCacheKeys.QUALIFYING_LIFE_EVENTS] })
  queryClient.removeQueries({ queryKey: memberBenefitsKeys.all })
  queryClient.removeQueries({ queryKey: healthWalletKeys.all })
  // Reset by name too: a store not yet created in this page load is missed below, but its persisted copy (the
  // previous member's plans, location, and filters) would load on the next member's first care-nav visit.
  useCareNavStore().$reset()
  _clearAllStores()
}

async function identifyIntercomUser(userId: string | undefined) {
  if (!userId) return
  try {
    const intercomJwt = await fetchIntercomJwt(userId)
    const userStore = useUserStore()
    if (userStore.user?.id !== userId) return
    intercomService.identifyUser(userId, intercomJwt)
  } catch (error) {
    if (isCancelledError(error)) return
    Sentry.captureException(error)
  }
}

function handleUnauthenticatedApiResponse() {
  const userStore = useUserStore()
  if (userStore.isLoggedIn) {
    userStore.logout()
  }
}

function handleAccessTokenRefreshed(data: { newAccessToken: string; newRefreshToken: string }) {
  const userStore = useUserStore()
  if (userStore.isLoggedIn) {
    userStore.setAuthTokens(data.newAccessToken, data.newRefreshToken)
  }
}

function _clearAllStores() {
  // @ts-ignore -- clear all stores
  getActivePinia()._s.forEach((store) => store.$reset())
}

export default {
  handleLogInEvent,
  handleLogOutEvent,
  handleUnauthenticatedApiResponse,
  handleAccessTokenRefreshed,
  identifyIntercomUser
}
